/**
 * Activity Log Renderer
 *
 * Renders activity log entries as formatted text/HTML
 */

export interface ActivityLogConfig {
  showTimestamp?: boolean;
  showLineNumbers?: boolean;
  maxEntries?: number;
}

/**
 * Render activity log as text
 */
export function renderActivityLog(activities: string[], config: ActivityLogConfig = {}): string {
  if (!activities || activities.length === 0) return '[No activity]';

  const lines = activities
    .slice(0, config.maxEntries)
    .map((entry, idx) => {
      const lineNum = config.showLineNumbers ? `${idx + 1}. ` : '';
      return `${lineNum}${entry}`;
    });

  return lines.join('\n');
}

/**
 * Render activity log as HTML
 */
export function renderActivityLogHtml(activities: string[], config: ActivityLogConfig = {}): string {
  if (!activities || activities.length === 0) return '<div class="activity-log-empty">[No activity]</div>';

  const entries = activities
    .slice(0, config.maxEntries)
    .map((entry, idx) => {
      const lineNum = config.showLineNumbers ? `<span class="activity-line-num">${idx + 1}.</span>` : '';
      return `<div class="activity-entry">${lineNum} ${entry}</div>`;
    })
    .join('');

  return `<div class="activity-log">${entries}</div>`;
}

/**
 * Format activity log with metadata
 */
export function formatActivityLog(activities: string[] | undefined, config: ActivityLogConfig = {}): {
  count: number;
  entries: string[];
  formatted: string;
} {
  const entries = activities || [];
  const count = entries.length;
  const formatted = renderActivityLog(entries, config);

  return {
    count,
    entries,
    formatted,
  };
}

/**
 * Get activity log summary
 */
export function getActivityLogSummary(activities: string[] | undefined): {
  count: number;
  isEmpty: boolean;
  preview: string[];
} {
  if (!activities || activities.length === 0) {
    return {
      count: 0,
      isEmpty: true,
      preview: [],
    };
  }

  return {
    count: activities.length,
    isEmpty: false,
    preview: activities.slice(0, 3),
  };
}
